import { Box, Typography, Card, CardContent, Chip } from '@mui/material';
import { useState } from 'react';
import { Bracket, User, Tournament } from '../../services/api';
import MatchScoreModal from '../matches/MatchScoreModal';

interface BracketDisplayProps {
  bracket: Bracket;
  users: User[];
  tournament: Tournament;
}

const BracketDisplay = ({ bracket, users, tournament }: BracketDisplayProps) => {
  const [selectedMatch, setSelectedMatch] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleMatchClick = (match: any) => {
    setSelectedMatch(match);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMatch(null);
  };
  
  const getUserName = (userId?: string) => {
    if (!userId) return 'TBD';
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  };

  // Group matches by round
  const matchesByRound = bracket.matches.reduce((acc, match) => {
    if (!acc[match.round]) {
      acc[match.round] = [];
    }
    acc[match.round].push(match);
    return acc;
  }, {} as Record<number, typeof bracket.matches>);

  const rounds = Object.keys(matchesByRound).map(Number).sort((a, b) => a - b);
  const maxRound = Math.max(...rounds, 0);

  const getRoundName = (round: number) => {
    if (round === maxRound) return 'Final';
    if (round === maxRound - 1) return 'Semi-Finals';
    if (round === maxRound - 2) return 'Quarter-Finals';
    return `Round ${round}`;
  };

  // Single Elimination Bracket with proper bracket lines
  if (bracket.type === 'single_elimination') {
    const MATCH_HEIGHT = 100;
    const MATCH_GAP = 40;
    const CONNECTOR_WIDTH = 60;
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Tournament Bracket
        </Typography>
        
        <Box sx={{ 
          position: 'relative',
          overflowX: 'auto',
          pb: 3,
          pt: 2
        }}>
          <Box sx={{ display: 'flex', position: 'relative', minHeight: 400 }}>
            {rounds.map((round, roundIndex) => {
              const matches = matchesByRound[round] || [];
              const isLastRound = roundIndex === rounds.length - 1;
              const spacing = Math.pow(2, roundIndex) * (MATCH_HEIGHT + MATCH_GAP);
              
              return (
                <Box key={round} sx={{ position: 'relative', mr: `${CONNECTOR_WIDTH}px` }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600,
                      mb: 2,
                      textAlign: 'center',
                      color: 'primary.main',
                      width: '280px'
                    }}
                  >
                    {getRoundName(round)}
                  </Typography>
                  
                  {matches.map((match, matchIndex) => {
                    const topPosition = matchIndex * spacing + (spacing / 2) - (MATCH_HEIGHT / 2);
                    
                    return (
                      <Box
                        key={match.id}
                        sx={{
                          position: 'absolute',
                          top: `${topPosition}px`,
                          width: '280px'
                        }}
                      >
                        <Card 
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 4,
                              transform: 'translateY(-2px)'
                            }
                          }}
                          onClick={() => handleMatchClick(match)}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {/* Player 1 */}
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1.5,
                                bgcolor: match.status === 'completed' && match.winner_id === match.player1_id ? 'success.50' : 'grey.50',
                                borderRadius: 1,
                                border: match.status === 'completed' && match.winner_id === match.player1_id ? '2px solid' : '1px solid',
                                borderColor: match.status === 'completed' && match.winner_id === match.player1_id ? 'success.main' : 'grey.300',
                              }}>
                                <Typography 
                                  variant="body2"
                                  sx={{ 
                                    fontWeight: match.status === 'completed' && match.winner_id === match.player1_id ? 600 : 400,
                                    color: match.status === 'completed' && match.winner_id === match.player1_id ? 'success.dark' : 'text.primary'
                                  }}
                                >
                                  {getUserName(match.player1_id)}
                                </Typography>
                                {match.status === 'completed' && match.winner_id === match.player1_id && (
                                  <Chip label="W" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                              </Box>

                              {/* Player 2 */}
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1.5,
                                bgcolor: match.status === 'completed' && match.winner_id === match.player2_id ? 'success.50' : 'grey.50',
                                borderRadius: 1,
                                border: match.status === 'completed' && match.winner_id === match.player2_id ? '2px solid' : '1px solid',
                                borderColor: match.status === 'completed' && match.winner_id === match.player2_id ? 'success.main' : 'grey.300',
                              }}>
                                <Typography 
                                  variant="body2"
                                  sx={{ 
                                    fontWeight: match.status === 'completed' && match.winner_id === match.player2_id ? 600 : 400,
                                    color: match.status === 'completed' && match.winner_id === match.player2_id ? 'success.dark' : 'text.primary'
                                  }}
                                >
                                  {getUserName(match.player2_id)}
                                </Typography>
                                {match.status === 'completed' && match.winner_id === match.player2_id && (
                                  <Chip label="W" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                              </Box>
                            </Box>

                            {match.status === 'completed' && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Completed
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                        
                        {/* Draw connector line to next round */}
                        {!isLastRound && (
                          <svg
                            style={{
                              position: 'absolute',
                              left: '280px',
                              top: `${MATCH_HEIGHT / 2}px`,
                              width: `${CONNECTOR_WIDTH}px`,
                              height: '2px',
                              overflow: 'visible',
                              pointerEvents: 'none',
                            }}
                          >
                            <line
                              x1="0"
                              y1="0"
                              x2={CONNECTOR_WIDTH / 2}
                              y2="0"
                              stroke="#bbb"
                              strokeWidth="2"
                            />
                          </svg>
                        )}
                      </Box>
                    );
                  })}
                  
                  {/* Draw vertical and horizontal connectors between match pairs */}
                  {!isLastRound && matches.length > 1 && (
                    <svg
                      style={{
                        position: 'absolute',
                        left: '280px',
                        top: 0,
                        width: `${CONNECTOR_WIDTH}px`,
                        height: '100%',
                        pointerEvents: 'none',
                      }}
                    >
                      {matches.map((_match, matchIndex) => {
                        if (matchIndex % 2 === 1) return null; // Only draw for first match of each pair
                        
                        const match1Center = matchIndex * spacing + (spacing / 2);
                        const match2Center = (matchIndex + 1) * spacing + (spacing / 2);
                        const midPoint = (match1Center + match2Center) / 2;
                        
                        return (
                          <g key={matchIndex}>
                            {/* Vertical line connecting two matches */}
                            <line
                              x1={CONNECTOR_WIDTH / 2}
                              y1={match1Center}
                              x2={CONNECTOR_WIDTH / 2}
                              y2={match2Center}
                              stroke="#bbb"
                              strokeWidth="2"
                            />
                            {/* Horizontal line to next round */}
                            <line
                              x1={CONNECTOR_WIDTH / 2}
                              y1={midPoint}
                              x2={CONNECTOR_WIDTH}
                              y2={midPoint}
                              stroke="#bbb"
                              strokeWidth="2"
                            />
                          </g>
                        );
                      })}
                    </svg>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
        
        {selectedMatch && (
          <MatchScoreModal
            open={modalOpen}
            onClose={handleCloseModal}
            match={selectedMatch}
            users={users}
            thirdSetFormat={tournament.third_set_format}
          />
        )}
      </Box>
    );
  }

  // Round Robin Pool Bracket - Show ONLY elimination bracket with proper bracket lines
  if (bracket.type === 'round_robin_pool' && bracket.matches) {
    const MATCH_HEIGHT = 100;
    const MATCH_GAP = 40;
    const CONNECTOR_WIDTH = 60;
    
    // Helper to get pool name from pool ID
    const getPoolName = (playerId: string) => {
      if (playerId.startsWith('pool_') && playerId.endsWith('_winner')) {
        const poolId = playerId.substring(5, playerId.length - 7);
        const pool = bracket.pools?.find(p => p.id === poolId);
        return pool ? `${pool.name} Winner` : 'TBD';
      }
      return getUserName(playerId);
    };
    
    // Group matches by round (skip bye matches)
    const rrMatchesByRound = bracket.matches.reduce((acc, match) => {
      if (match.status === 'bye') return acc;
      if (!acc[match.round]) {
        acc[match.round] = [];
      }
      acc[match.round].push(match);
      return acc;
    }, {} as Record<number, typeof bracket.matches>);

    const rrRounds = Object.keys(rrMatchesByRound).map(Number).sort((a, b) => a - b);
    const rrMaxRound = Math.max(...rrRounds, 0);

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Tournament Bracket
        </Typography>
        
        <Box sx={{ 
          position: 'relative',
          overflowX: 'auto',
          pb: 3,
          pt: 2
        }}>
          <Box sx={{ display: 'flex', position: 'relative', minHeight: 400 }}>
            {rrRounds.map((round, roundIndex) => {
              const matches = rrMatchesByRound[round] || [];
              const isLastRound = roundIndex === rrRounds.length - 1;
              const spacing = Math.pow(2, roundIndex) * (MATCH_HEIGHT + MATCH_GAP);
              
              return (
                <Box key={round} sx={{ position: 'relative', mr: `${CONNECTOR_WIDTH}px` }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600,
                      mb: 2,
                      textAlign: 'center',
                      color: 'primary.main',
                      width: '280px'
                    }}
                  >
                    {round === rrMaxRound ? 'Final' : 
                     round === rrMaxRound - 1 ? 'Semi-Finals' : 
                     round === rrMaxRound - 2 ? 'Quarter-Finals' : 
                     `Round ${round}`}
                  </Typography>
                  
                  {matches.map((match, matchIndex) => {
                    const topPosition = matchIndex * spacing + (spacing / 2) - (MATCH_HEIGHT / 2);
                    const canClick = match.player1_id && match.player2_id && 
                                    !match.player1_id.startsWith('pool_') && 
                                    !match.player2_id.startsWith('pool_');
                    
                    return (
                      <Box
                        key={match.id}
                        sx={{
                          position: 'absolute',
                          top: `${topPosition}px`,
                          width: '280px'
                        }}
                      >
                        <Card 
                          sx={{ 
                            cursor: canClick ? 'pointer' : 'default',
                            transition: 'all 0.2s',
                            '&:hover': canClick ? {
                              boxShadow: 4,
                              transform: 'translateY(-2px)'
                            } : {}
                          }}
                          onClick={() => canClick && handleMatchClick(match)}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {/* Player 1 */}
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1.5,
                                bgcolor: match.status === 'completed' && match.winner_id === match.player1_id ? 'success.50' : 'grey.50',
                                borderRadius: 1,
                                border: match.status === 'completed' && match.winner_id === match.player1_id ? '2px solid' : '1px solid',
                                borderColor: match.status === 'completed' && match.winner_id === match.player1_id ? 'success.main' : 'grey.300',
                              }}>
                                <Typography 
                                  variant="body2"
                                  sx={{ 
                                    fontWeight: match.status === 'completed' && match.winner_id === match.player1_id ? 600 : 400,
                                    fontStyle: match.player1_id?.startsWith('pool_') ? 'italic' : 'normal',
                                    color: match.status === 'completed' && match.winner_id === match.player1_id ? 'success.dark' : 'text.primary'
                                  }}
                                >
                                  {match.player1_id ? getPoolName(match.player1_id) : 'TBD'}
                                </Typography>
                                {match.status === 'completed' && match.winner_id === match.player1_id && (
                                  <Chip label="W" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                              </Box>

                              {/* Player 2 */}
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1.5,
                                bgcolor: match.status === 'completed' && match.winner_id === match.player2_id ? 'success.50' : 'grey.50',
                                borderRadius: 1,
                                border: match.status === 'completed' && match.winner_id === match.player2_id ? '2px solid' : '1px solid',
                                borderColor: match.status === 'completed' && match.winner_id === match.player2_id ? 'success.main' : 'grey.300',
                              }}>
                                <Typography 
                                  variant="body2"
                                  sx={{ 
                                    fontWeight: match.status === 'completed' && match.winner_id === match.player2_id ? 600 : 400,
                                    fontStyle: match.player2_id?.startsWith('pool_') ? 'italic' : 'normal',
                                    color: match.status === 'completed' && match.winner_id === match.player2_id ? 'success.dark' : 'text.primary'
                                  }}
                                >
                                  {match.player2_id ? getPoolName(match.player2_id) : 'TBD'}
                                </Typography>
                                {match.status === 'completed' && match.winner_id === match.player2_id && (
                                  <Chip label="W" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                              </Box>
                            </Box>

                            {match.status === 'completed' && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Completed
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                        
                        {/* Draw connector line to next round */}
                        {!isLastRound && (
                          <svg
                            style={{
                              position: 'absolute',
                              left: '280px',
                              top: `${MATCH_HEIGHT / 2}px`,
                              width: `${CONNECTOR_WIDTH}px`,
                              height: '2px',
                              overflow: 'visible',
                              pointerEvents: 'none',
                            }}
                          >
                            <line
                              x1="0"
                              y1="0"
                              x2={CONNECTOR_WIDTH / 2}
                              y2="0"
                              stroke="#bbb"
                              strokeWidth="2"
                            />
                          </svg>
                        )}
                      </Box>
                    );
                  })}
                  
                  {/* Draw vertical and horizontal connectors between match pairs */}
                  {!isLastRound && matches.length > 1 && (
                    <svg
                      style={{
                        position: 'absolute',
                        left: '280px',
                        top: 0,
                        width: `${CONNECTOR_WIDTH}px`,
                        height: '100%',
                        pointerEvents: 'none',
                      }}
                    >
                      {matches.map((_match, matchIndex) => {
                        if (matchIndex % 2 === 1) return null; // Only draw for first match of each pair
                        
                        const match1Center = matchIndex * spacing + (spacing / 2);
                        const match2Center = (matchIndex + 1) * spacing + (spacing / 2);
                        const midPoint = (match1Center + match2Center) / 2;
                        
                        return (
                          <g key={matchIndex}>
                            {/* Vertical line connecting two matches */}
                            <line
                              x1={CONNECTOR_WIDTH / 2}
                              y1={match1Center}
                              x2={CONNECTOR_WIDTH / 2}
                              y2={match2Center}
                              stroke="#bbb"
                              strokeWidth="2"
                            />
                            {/* Horizontal line to next round */}
                            <line
                              x1={CONNECTOR_WIDTH / 2}
                              y1={midPoint}
                              x2={CONNECTOR_WIDTH}
                              y2={midPoint}
                              stroke="#bbb"
                              strokeWidth="2"
                            />
                          </g>
                        );
                      })}
                    </svg>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
        
        {selectedMatch && (
          <MatchScoreModal
            open={modalOpen}
            onClose={handleCloseModal}
            match={selectedMatch}
            users={users}
            thirdSetFormat={tournament.third_set_format}
          />
        )}
      </Box>
    );
  }

  return (
    <>
      <Typography color="text.secondary">
        No bracket available for this tournament type
      </Typography>
      
      {selectedMatch && (
        <MatchScoreModal
          open={modalOpen}
          onClose={handleCloseModal}
          match={selectedMatch}
          users={users}
          thirdSetFormat={tournament.third_set_format}
        />
      )}
    </>
  );
};

export default BracketDisplay;
